import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { api } from '../utils/api';
import { useToast } from './Toast';
import { MOIS } from '../utils/dates';
import { defaultPermissions, permissionsAfterRoleChange, ROLE_LABELS } from '../utils/userPermissions';

export function UserManagementModal({ isOpen, onClose, stores, onRefresh }) {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const emptyForm = { username: '', email: '', password: '', role: 'user', color: '#3B82F6', storeIds: [], permissions: {} };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { if (isOpen) loadUsers(); }, [isOpen]);
  const loadUsers = async () => { try { setUsers(await api.getUsers()); } catch (e) { toast(e.message, 'error'); } };
  const resetForm = () => { setForm(emptyForm); setEditUser(null); };

  const ROLE_NUM = { admin: 1, both: 1, responsable: 2, user: 3 };
  const roleNum = ROLE_NUM[form.role] || 3;

  const handleEdit = (u) => {
    setEditUser(u);
    const perms = {};
    u.stores.forEach(s => {
      const p = {};
      ['visible_planning','view_planning','view_chiffres','view_dluo','view_stocks','manage_planning','manage_objectives','manage_kpi','mobile_full_access'].forEach(k => { p[k] = s[k] || 0; });
      perms[s.id] = p;
    });
    setForm({ username: u.username, email: u.email, password: '', role: u.role, color: u.color, storeIds: u.stores.map(s => s.id), permissions: perms });
  };

  const handleSave = async () => {
    try {
      if (editUser) { await api.updateUser(editUser.id, form); toast('Mis à jour', 'success'); }
      else { if (!form.password) { toast('Mot de passe requis', 'error'); return; } await api.createUser(form); toast('Créé', 'success'); }
      resetForm(); loadUsers(); onRefresh?.();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Désactiver ?')) return;
    try { await api.deleteUser(id); toast('Désactivé', 'success'); loadUsers(); onRefresh?.(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const toggleStore = (sid) => {
    setForm(prev => {
      const ids = prev.storeIds.includes(sid) ? prev.storeIds.filter(i => i !== sid) : [...prev.storeIds, sid];
      const perms = { ...prev.permissions };
      if (!perms[sid]) perms[sid] = defaultPermissions(prev.role);
      return { ...prev, storeIds: ids, permissions: perms };
    });
  };

  const togglePerm = (sid, field) => {
    setForm(prev => {
      const perms = { ...prev.permissions };
      if (!perms[sid]) perms[sid] = defaultPermissions(prev.role);
      const nextValue = perms[sid][field] ? 0 : 1;
      const nextStorePerms = { ...perms[sid], [field]: nextValue };
      if (nextValue && (field === 'manage_planning' || field === 'manage_objectives')) nextStorePerms.view_planning = 1;
      if (nextValue && field === 'manage_kpi') nextStorePerms.view_chiffres = 1;
      if (!nextValue && field === 'view_planning') {
        nextStorePerms.manage_planning = 0;
        nextStorePerms.manage_objectives = 0;
      }
      if (!nextValue && field === 'view_chiffres') nextStorePerms.manage_kpi = 0;
      perms[sid] = nextStorePerms;
      return { ...prev, permissions: perms };
    });
  };

  const handleRoleChange = (newRole) => {
    if (newRole === form.role) return;
    if (editUser) {
      const currentLabel = ROLE_LABELS[form.role] || form.role;
      const nextLabel = ROLE_LABELS[newRole] || newRole;
      const warning = newRole === 'user'
        ? '\n\nLes droits de gestion seront retirés. Les droits de visibilité par magasin seront conservés.'
        : '\n\nLes droits actuels par magasin seront conservés.';
      if (!window.confirm(`Changer le rôle de « ${form.username} » de ${currentLabel} à ${nextLabel} ?${warning}`)) return;
    }
    setForm(prev => ({
      ...prev,
      role: newRole,
      permissions: permissionsAfterRoleChange(prev.permissions, prev.storeIds, newRole),
    }));
  };

  const VISIBILITY_FIELDS = [
    { key: 'view_planning', label: '📋 Planning' },
    { key: 'view_chiffres', label: '📊 Chiffres' },
    { key: 'view_dluo', label: '📦 DLUO' },
    { key: 'view_stocks', label: '🏷️ Stocks' },
  ];

  const RESPONSABLE_FIELDS = [
    { key: 'manage_planning', label: '📋 Gestion du planning' },
    { key: 'manage_objectives', label: '🎯 Gestion objectifs mensuels' },
    { key: 'manage_kpi', label: '📊 Menu gestion des chiffres' },
    { key: 'mobile_full_access', label: '📱 Tout voir sur téléphone' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={() => { onClose(); resetForm(); }} title="Gestion des utilisateurs" wide>
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20, minHeight: 400 }}>
        {/* Left: user list */}
        <div style={{ borderRight: '1px solid var(--border-light)', paddingRight: 16 }}>
          <h3 style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Utilisateurs</h3>
          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            {users.filter(u => u.active).map(u => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 'var(--radius-sm)', marginBottom: 4, background: editUser?.id === u.id ? 'rgba(79,143,255,0.1)' : 'transparent', cursor: 'pointer' }} onClick={() => handleEdit(u)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: u.color }} />
                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{u.username}</span>
                  <span className="badge" style={{ fontSize: '0.6rem' }}>{ROLE_LABELS[u.role] || u.role}</span>
                </div>
                <button className="btn-icon" onClick={(e) => { e.stopPropagation(); handleDelete(u.id); }} style={{ fontSize: '0.75rem', color: 'var(--accent-red)' }}>✕</button>
              </div>
            ))}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={resetForm} style={{ marginTop: 12, width: '100%' }}>+ Nouveau</button>
        </div>

        {/* Right: form */}
        <div style={{ maxHeight: 550, overflowY: 'auto', paddingRight: 4 }}>
          <h3 style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{editUser ? 'Modifier' : 'Créer'}</h3>

          {/* Identity */}
          <div className="user-mgmt-section">
            <div className="user-mgmt-section-title">👤 Identité</div>
            <div className="form-group" style={{ marginBottom: 6 }}><label>Nom d'utilisateur</label><input className="form-input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} /></div>
            <div className="form-group" style={{ marginBottom: 6 }}><label>Email</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="form-group" style={{ marginBottom: 6 }}><label>Mot de passe {editUser && '(vide = inchangé)'}</label><input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
            <div className="form-row">
              <div className="form-group"><label>Rôle</label>
                <select className="form-select" value={form.role} onChange={e => handleRoleChange(e.target.value)}>
                  <option value="admin">⚡ Admin (1)</option>
                  <option value="both">⚡ Admin + Utilisateur</option>
                  <option value="responsable">🔑 Responsable (2)</option>
                  <option value="user">👤 Utilisateur (3)</option>
                </select>
              </div>
              <div className="form-group"><label>Couleur</label>
                <div className="color-picker-wrapper"><input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} /><span style={{ fontSize: '0.8rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{form.color}</span></div>
              </div>
            </div>
          </div>

          {/* Stores & permissions */}
          <div className="user-mgmt-section">
            <div className="user-mgmt-section-title">🏪 Magasins & Droits</div>
            {stores.map(s => {
              const active = form.storeIds.includes(s.id);
              const p = form.permissions[s.id] || {};
              return (
                <div key={s.id} className="user-mgmt-store" style={{ borderColor: active ? 'var(--accent-blue)' : 'var(--border-light)' }}>
                  <label className="form-checkbox" style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 6 }}>
                    <input type="checkbox" checked={active} onChange={() => toggleStore(s.id)} />
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block' }} />{s.name}
                    </span>
                  </label>

                  {active && (
                    <div style={{ paddingLeft: 8, marginTop: 4 }}>
                      {/* Visible on planning */}
                      <label className="form-checkbox user-mgmt-perm"><input type="checkbox" checked={!!p.visible_planning} onChange={() => togglePerm(s.id, 'visible_planning')} /> 📋 Visible sur planning</label>

                      {/* Page visibility */}
                      <div className="user-mgmt-perm-group">
                        <div className="user-mgmt-perm-group-title">Visibilité des pages</div>
                        <div className="user-mgmt-perm-grid">
                          {VISIBILITY_FIELDS.map(f => (
                            <label key={f.key} className="form-checkbox user-mgmt-perm">
                              <input type="checkbox" checked={!!p[f.key]} onChange={() => togglePerm(s.id, f.key)} /> {f.label}
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Management (responsable only) */}
                      {roleNum <= 2 && (
                        <div className="user-mgmt-perm-group">
                          <div className="user-mgmt-perm-group-title">Gestion (responsable)</div>
                          <div className="user-mgmt-perm-grid">
                            {RESPONSABLE_FIELDS.map(f => (
                              <label key={f.key} className="form-checkbox user-mgmt-perm">
                                <input type="checkbox" checked={!!p[f.key]} onChange={() => togglePerm(s.id, f.key)} /> {f.label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="modal-actions" style={{ borderTop: 'none', paddingTop: 0 }}>
            <button className="btn btn-secondary" onClick={resetForm}>Annuler</button>
            <button className="btn btn-primary" onClick={handleSave}>{editUser ? 'Mettre à jour' : 'Créer'}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function StoreManagementModal({ isOpen, onClose, onRefresh }) {
  const toast = useToast();
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({ name: '', color: '#6366F1', opening_time: '09:00', closing_time: '19:00' });
  const [editStore, setEditStore] = useState(null);

  useEffect(() => { if (isOpen) loadStores(); }, [isOpen]);
  const loadStores = async () => { try { setStores(await api.getStores()); } catch (e) { toast(e.message, 'error'); } };

  const resetForm = () => { setForm({ name: '', color: '#6366F1', opening_time: '09:00', closing_time: '19:00' }); setEditStore(null); };

  const handleSave = async () => {
    try {
      if (editStore) { await api.updateStore(editStore.id, form); toast('Magasin mis à jour', 'success'); }
      else { await api.createStore(form); toast('Magasin créé', 'success'); }
      resetForm(); loadStores(); onRefresh?.();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce magasin ?')) return;
    try { await api.deleteStore(id); toast('Magasin supprimé', 'success'); loadStores(); onRefresh?.(); }
    catch (e) { toast(e.message, 'error'); }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { onClose(); resetForm(); }} title="Gestion des magasins">
      <div style={{ maxHeight: 250, overflowY: 'auto', marginBottom: 16 }}>
        {stores.map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: editStore?.id === s.id ? 'rgba(79,143,255,0.1)' : 'var(--bg-surface)', borderRadius: 'var(--radius-sm)', marginBottom: 6, cursor: 'pointer' }}
            onClick={() => { setEditStore(s); setForm({ name: s.name, color: s.color, opening_time: s.opening_time, closing_time: s.closing_time }); }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: s.color }} />
              <span style={{ fontWeight: 500 }}>{s.name}</span>
              <span style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{s.opening_time}-{s.closing_time}</span>
            </div>
            <button className="btn-icon" onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }} style={{ fontSize: '0.75rem', color: 'var(--accent-red)' }}>✕</button>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 16 }}>
        <h3 style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 12 }}>{editStore ? 'Modifier' : 'Nouveau'} magasin</h3>
        <div className="form-group"><label>Nom</label><input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
        <div className="form-row">
          <div className="form-group"><label>Ouverture</label><input className="form-input" type="time" value={form.opening_time} onChange={e => setForm({ ...form, opening_time: e.target.value })} /></div>
          <div className="form-group"><label>Fermeture</label><input className="form-input" type="time" value={form.closing_time} onChange={e => setForm({ ...form, closing_time: e.target.value })} /></div>
        </div>
        <div className="form-group"><label>Couleur</label><div className="color-picker-wrapper"><input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} /></div></div>
        <div className="modal-actions"><button className="btn btn-secondary" onClick={resetForm}>Annuler</button><button className="btn btn-primary" onClick={handleSave}>{editStore ? 'Mettre à jour' : 'Créer'}</button></div>
      </div>
    </Modal>
  );
}

export function ObjectivesModal({ isOpen, onClose, storeId, month, year, onRefresh }) {
  const toast = useToast();
  const [caHt, setCaHt] = useState('');
  const [pmHt, setPmHt] = useState('');
  const [freeLines, setFreeLines] = useState([]);

  useEffect(() => {
    if (isOpen && storeId) {
      api.getObjectives(storeId, month, year).then(data => {
        // line_order 1 = CA HT, 2 = PM HT, 3+ = free
        const caLine = data.find(d => d.line_order === 1);
        const pmLine = data.find(d => d.line_order === 2);
        setCaHt(caLine?.value_text || '');
        setPmHt(pmLine?.value_text || '');
        setFreeLines(data.filter(d => d.line_order >= 3).map(d => ({
          line_order: d.line_order,
          objective_text: d.objective_text,
          value_text: d.value_text
        })));
      }).catch(e => toast(e.message, 'error'));
    }
  }, [isOpen, storeId, month, year]);

  const addFreeLine = () => {
    if (freeLines.length >= 4) return;
    setFreeLines(prev => [...prev, { line_order: prev.length + 3, objective_text: '', value_text: '' }]);
  };
  const removeFreeLine = (idx) => {
    setFreeLines(prev => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, line_order: i + 3 })));
  };
  const updateFreeLine = (idx, field, val) => {
    setFreeLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: val } : l));
  };

  const handleSave = async () => {
    const lines = [
      { line_order: 1, objective_text: 'CA HT', value_text: caHt },
      { line_order: 2, objective_text: 'PM HT', value_text: pmHt },
      ...freeLines.map((l, i) => ({ ...l, line_order: i + 3 }))
    ];
    try {
      await api.saveObjectives({ store_id: storeId, month, year, lines });
      toast('Objectifs sauvegardés', 'success');
      onRefresh?.(); onClose();
    } catch (e) { toast(e.message, 'error'); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`🎯 Objectifs ${MOIS[month - 1]} ${year}`}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>CA HT (€)</label>
            <input className="form-input" type="number" step="0.01" placeholder="ex: 30000" value={caHt} onChange={e => setCaHt(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>PM HT (€)</label>
            <input className="form-input" type="number" step="0.01" placeholder="ex: 22.50" value={pmHt} onChange={e => setPmHt(e.target.value)} />
          </div>
        </div>
      </div>

      {freeLines.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 12, marginBottom: 12 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>Objectifs libres</div>
          {freeLines.map((line, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <input className="form-input" placeholder="Intitulé" value={line.objective_text} onChange={e => updateFreeLine(idx, 'objective_text', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <input className="form-input" placeholder="Valeur" value={line.value_text} onChange={e => updateFreeLine(idx, 'value_text', e.target.value)} />
              </div>
              <button className="btn-icon" onClick={() => removeFreeLine(idx)} style={{ color: 'var(--accent-red)', marginBottom: 1 }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {freeLines.length < 4 && (
        <button className="btn btn-secondary btn-sm" onClick={addFreeLine} style={{ marginTop: 4, marginBottom: 12 }}>+ Objectif libre</button>
      )}

      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" onClick={handleSave}>Sauvegarder</button>
      </div>
    </Modal>
  );
}

export function SlotModal({ isOpen, onClose, storeId, storeUsers, defaultDate, defaultTime, editSlot, onRefresh }) {
  const toast = useToast();
  const [form, setForm] = useState({ user_id: '', date: '', start_time: '', end_time: '', status: 'present' });
  const [overlapInfo, setOverlapInfo] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setOverlapInfo(null);
      if (editSlot) {
        setForm({ user_id: editSlot.user_id, date: editSlot.date, start_time: editSlot.start_time, end_time: editSlot.end_time, status: editSlot.status });
      } else {
        const endH = defaultTime ? parseInt(defaultTime.split(':')[0]) : 9;
        const endM = defaultTime ? parseInt(defaultTime.split(':')[1]) + 30 : 30;
        const et = `${String(endM >= 60 ? endH + 1 : endH).padStart(2, '0')}:${String(endM % 60).padStart(2, '0')}`;
        setForm({ user_id: storeUsers[0]?.id || '', date: defaultDate || '', start_time: defaultTime || '09:00', end_time: et, status: 'present' });
      }
    }
  }, [isOpen, editSlot, defaultDate, defaultTime, storeUsers]);

  const handleSave = async (forceOverlap = false) => {
    if (!form.user_id || !form.date || !form.start_time || !form.end_time) { toast('Tous les champs sont requis', 'error'); return; }
    try {
      if (editSlot) { 
        await api.updateSlot(editSlot.id, { ...form, force_overlap: forceOverlap }); 
        toast('Créneau modifié', 'success'); 
      } else { 
        await api.createSlot({ ...form, store_id: storeId, force_overlap: forceOverlap }); 
        toast('Créneau créé', 'success'); 
      }
      setOverlapInfo(null);
      onRefresh?.(); onClose();
    } catch (e) {
      // Check if it's an overlap error (409)
      if (e.overlapData) {
        setOverlapInfo(e.overlapData);
      } else {
        toast(e.message, 'error');
      }
    }
  };

  const handleForceOverlap = async () => {
    await handleSave(true);
  };

  const handleDelete = async () => {
    if (!editSlot) return;
    try { await api.deleteSlot(editSlot.id); toast('Créneau supprimé', 'success'); onRefresh?.(); onClose(); }
    catch (e) { toast(e.message, 'error'); }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { setOverlapInfo(null); onClose(); }} title={editSlot ? 'Modifier le créneau' : 'Ajouter un créneau'}>
      {overlapInfo ? (
        <div>
          <div style={{ background: 'var(--absent-bg)', border: '1px solid var(--accent-red)', borderRadius: 'var(--radius-sm)', padding: '14px', marginBottom: '16px' }}>
            <div style={{ fontWeight: 700, color: 'var(--accent-red)', marginBottom: 8, fontSize: '0.9rem' }}>⚠️ Chevauchement détecté</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)', marginBottom: 8 }}>
              Le nouveau créneau <strong>{form.start_time} → {form.end_time}</strong> ({form.status}) chevauche :
            </div>
            {overlapInfo.overlapping_slots.map(s => (
              <div key={s.id} style={{ padding: '8px 12px', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', marginBottom: 6, fontSize: '0.85rem' }}>
                <strong>{s.username}</strong> : {s.start_time} → {s.end_time} ({s.status === 'present' ? '✅ Présent' : s.status === 'absent' ? '❌ Absent' : '🏖️ Congé'})
              </div>
            ))}
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 8 }}>
              En validant, les plages existantes seront automatiquement ajustées (coupées ou supprimées) pour éviter le chevauchement.
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setOverlapInfo(null)}>← Modifier mon créneau</button>
            <button className="btn btn-danger" onClick={handleForceOverlap}>Valider et écraser</button>
          </div>
        </div>
      ) : (
        <>
          <div className="form-group"><label>Utilisateur</label>
            <select className="form-select" value={form.user_id} onChange={e => setForm({ ...form, user_id: parseInt(e.target.value) })}>
              <option value="">-- Choisir --</option>
              {storeUsers.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Date</label><input className="form-input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
          <div className="form-row">
            <div className="form-group"><label>Début</label><input className="form-input" type="time" step="1800" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} /></div>
            <div className="form-group"><label>Fin</label><input className="form-input" type="time" step="1800" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} /></div>
          </div>
          <div className="form-group"><label>Statut</label>
            <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="present">✅ Présent</option><option value="absent">❌ Absent</option><option value="conge">🏖️ Congé</option>
            </select>
          </div>
          <div className="modal-actions">
            {editSlot && <button className="btn btn-danger" onClick={handleDelete} style={{ marginRight: 'auto' }}>Supprimer</button>}
            <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button className="btn btn-primary" onClick={() => handleSave(false)}>{editSlot ? 'Modifier' : 'Ajouter'}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

export function NoteModal({ isOpen, onClose, storeId, date, editNote, onRefresh }) {
  const toast = useToast();
  const [content, setContent] = useState('');

  useEffect(() => { if (isOpen) setContent(editNote?.content || ''); }, [isOpen, editNote]);

  const handleSave = async () => {
    if (!content.trim()) { toast('Le contenu est requis', 'error'); return; }
    try {
      if (editNote) { await api.updateNote(editNote.id, content); toast('Note modifiée', 'success'); }
      else { await api.createNote({ store_id: storeId, date, content }); toast('Note ajoutée', 'success'); }
      onRefresh?.(); onClose();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleDelete = async () => {
    if (!editNote) return;
    try { await api.deleteNote(editNote.id); toast('Note supprimée', 'success'); onRefresh?.(); onClose(); }
    catch (e) { toast(e.message, 'error'); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editNote ? 'Modifier la note' : `Note du ${date}`}>
      <div className="form-group"><label>Contenu</label><textarea className="form-textarea" value={content} onChange={e => setContent(e.target.value)} rows={4} placeholder="Saisir la note..." /></div>
      <div className="modal-actions">
        {editNote && <button className="btn btn-danger" onClick={handleDelete} style={{ marginRight: 'auto' }}>Supprimer</button>}
        <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary" onClick={handleSave}>{editNote ? 'Modifier' : 'Ajouter'}</button>
      </div>
    </Modal>
  );
}

export function DuplicateModal({ isOpen, onClose, type, storeId, weekStart, weekEnd, onRefresh }) {
  const toast = useToast();
  const [targetDate, setTargetDate] = useState('');
  const [sourceDate, setSourceDate] = useState('');
  const [conflict, setConflict] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSourceDate(weekStart || '');
      setTargetDate('');
      setConflict(null);
      setIsSubmitting(false);
    }
  }, [isOpen, weekStart]);

  const addDays = (date, days) => {
    const result = new Date(`${date}T00:00:00.000Z`);
    result.setUTCDate(result.getUTCDate() + days);
    return result.toISOString().split('T')[0];
  };

  const formatDate = (date) => new Date(`${date}T00:00:00.000Z`).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
  });

  const handleDuplicate = async (mode) => {
    if (!targetDate) { toast('Sélectionnez une date cible', 'error'); return; }
    if (!sourceDate) { toast('Sélectionnez une date source', 'error'); return; }
    if (sourceDate === targetDate) { toast('La source et la cible doivent être différentes', 'error'); return; }

    setIsSubmitting(true);
    try {
      if (type === 'day') {
        const payload = { store_id: storeId, source_date: sourceDate, target_date: targetDate };
        if (mode) payload.mode = mode;
        if (mode && conflict?.version) payload.target_version = conflict.version;
        const result = await api.duplicateDay(payload);
        toast(result.message, 'success');
      } else {
        const payload = {
          store_id: storeId,
          source_start: sourceDate,
          source_end: addDays(sourceDate, 6),
          target_start: targetDate,
        };
        if (mode) payload.mode = mode;
        if (mode && conflict?.version) payload.target_version = conflict.version;
        const result = await api.duplicateWeek(payload);
        toast(result.message, 'success');
      }
      onRefresh?.();
      onClose();
    } catch (e) {
      if (e.status === 409 && e.data?.error === 'target_not_empty') {
        setConflict({
          count: e.data.existing_count,
          start: e.data.target_start,
          end: e.data.target_end,
          version: e.data.target_version,
          changed: e.data.target_changed,
        });
      } else {
        toast(e.data?.message || e.message, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={type === 'day' ? 'Dupliquer une journée' : 'Dupliquer la semaine'}>
      {conflict ? (
        <div className="duplicate-conflict" role="alert">
          {conflict.changed && <p className="duplicate-conflict-updated">La cible a changé depuis votre dernier choix. Vérifiez son nouvel état avant de confirmer.</p>}
          <p className="duplicate-conflict-title">
            {type === 'day'
              ? `La journée du ${formatDate(conflict.start)} possède déjà ${conflict.count} créneau(x).`
              : `La semaine du ${formatDate(conflict.start)} au ${formatDate(conflict.end)} possède déjà ${conflict.count} créneau(x).`}
          </p>
          <p className="duplicate-conflict-question">Voulez-vous ajouter, remplacer ou annuler ?</p>
          <ul className="duplicate-conflict-help">
            <li><strong>Ajouter</strong> conserve les créneaux existants et ignore les doublons ou conflits.</li>
            <li><strong>Remplacer</strong> supprime les créneaux existants de la période cible.</li>
          </ul>
          <div className="modal-actions duplicate-choice-actions">
            <button className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>Annuler</button>
            <button className="btn btn-primary" onClick={() => handleDuplicate('add')} disabled={isSubmitting}>Ajouter</button>
            <button className="btn btn-danger" onClick={() => handleDuplicate('replace')} disabled={isSubmitting}>Remplacer</button>
          </div>
        </div>
      ) : type === 'day' ? (
        <>
          <div className="form-group"><label>Journée source</label><input className="form-input" type="date" value={sourceDate} onChange={e => { setSourceDate(e.target.value); setConflict(null); }} /></div>
          <div className="form-group"><label>Vers la date</label><input className="form-input" type="date" value={targetDate} onChange={e => { setTargetDate(e.target.value); setConflict(null); }} /></div>
        </>
      ) : (
        <>
          <div className="form-group"><label>Lundi de la semaine source</label><input className="form-input" type="date" value={sourceDate} onChange={e => { setSourceDate(e.target.value); setConflict(null); }} /></div>
          {sourceDate && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: -8, marginBottom: 12 }}>Semaine du {sourceDate} au {addDays(sourceDate, 6)}</p>}
          <div className="form-group"><label>Lundi de la semaine cible</label><input className="form-input" type="date" value={targetDate} onChange={e => { setTargetDate(e.target.value); setConflict(null); }} /></div>
        </>
      )}
      {!conflict && <div className="modal-actions"><button className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>Annuler</button><button className="btn btn-primary" onClick={() => handleDuplicate()} disabled={isSubmitting}>{isSubmitting ? 'Vérification…' : 'Dupliquer'}</button></div>}
    </Modal>
  );
}

export function ClearModal({ isOpen, onClose, type, storeId, storeUsers, date, weekStart, weekEnd, onRefresh }) {
  const toast = useToast();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [clearAll, setClearAll] = useState(true);
  const [clearDate, setClearDate] = useState(date || '');
  const [clearWeekStart, setClearWeekStart] = useState(weekStart || '');

  useEffect(() => {
    if (isOpen) {
      setClearDate(date || '');
      setClearWeekStart(weekStart || '');
      setSelectedUsers([]);
      setClearAll(true);
    }
  }, [isOpen, date, weekStart]);

  // Calculate week end from a Monday
  const getWeekEnd = (monday) => {
    if (!monday) return '';
    const d = new Date(monday);
    d.setDate(d.getDate() + 6);
    return d.toISOString().split('T')[0];
  };

  const toggleUser = (uid) => {
    setSelectedUsers(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const handleClear = async () => {
    try {
      const userIds = clearAll ? null : selectedUsers;
      if (type === 'day') {
        if (!clearDate) { toast('Sélectionnez une date', 'error'); return; }
        await api.clearDay({ store_id: storeId, date: clearDate, user_ids: userIds });
      } else {
        if (!clearWeekStart) { toast('Sélectionnez un lundi', 'error'); return; }
        await api.clearWeek({ store_id: storeId, start_date: clearWeekStart, end_date: getWeekEnd(clearWeekStart), user_ids: userIds });
      }
      toast('Planning effacé', 'success'); onRefresh?.(); onClose();
    } catch (e) { toast(e.message, 'error'); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={type === 'day' ? 'Effacer une journée' : 'Effacer une semaine'}>
      {type === 'day' ? (
        <div className="form-group">
          <label>Journée à effacer</label>
          <input className="form-input" type="date" value={clearDate} onChange={e => setClearDate(e.target.value)} />
        </div>
      ) : (
        <div className="form-group">
          <label>Lundi de la semaine à effacer</label>
          <input className="form-input" type="date" value={clearWeekStart} onChange={e => setClearWeekStart(e.target.value)} />
          {clearWeekStart && <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>Semaine du {clearWeekStart} au {getWeekEnd(clearWeekStart)}</p>}
        </div>
      )}
      <div className="form-group">
        <label className="form-checkbox"><input type="checkbox" checked={clearAll} onChange={() => setClearAll(!clearAll)} />Effacer tous les utilisateurs</label>
      </div>
      {!clearAll && (
        <div className="form-group"><label>Sélectionner les utilisateurs à retirer</label>
          {storeUsers.map(u => (
            <label key={u.id} className="form-checkbox" style={{ marginBottom: 6 }}>
              <input type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => toggleUser(u.id)} />
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: u.color }} />{u.username}</span>
            </label>
          ))}
        </div>
      )}
      <div className="modal-actions"><button className="btn btn-secondary" onClick={onClose}>Annuler</button><button className="btn btn-danger" onClick={handleClear}>Effacer</button></div>
    </Modal>
  );
}
